import type {
  TourOperatorSortField,
  TourOperatorType,
} from '@travel-planner/shared/schemas';
import {
  tourOperatorSortFields,
  tourOperatorTypeValues,
} from '@travel-planner/shared/schemas';

const tourOperatorTypeValueSet = new Set<string>(tourOperatorTypeValues);
const sortFieldValueSet = new Set<string>(tourOperatorSortFields);
const sortOrderValues = ['asc', 'desc'] as const;
type SortOrder = (typeof sortOrderValues)[number];
const sortOrderValueSet = new Set<string>(sortOrderValues);

export type TourOperatorsSearch = {
  search?: string;
  type?: TourOperatorType[];
  city?: string[];
  rating?: number[];
  specials?: string[];
  country?: string[];
  companyIds?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: TourOperatorSortField;
  sortOrder?: SortOrder;
};

const asStringArray = (v: unknown): string[] | undefined => {
  if (typeof v === 'string') return v.length > 0 ? [v] : undefined;
  return Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string' && x.length > 0)
    : undefined;
};

const asTypeArray = (v: unknown): TourOperatorType[] | undefined =>
  Array.isArray(v)
    ? v.filter(
        (x): x is TourOperatorType =>
          typeof x === 'string' && tourOperatorTypeValueSet.has(x)
      )
    : undefined;

const toNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string' || v.trim().length === 0) return null;
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : null;
};

const asNumberArray = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v)) {
    const parsed = toNumber(v);
    return parsed === null ? undefined : [parsed];
  }
  return v.map(toNumber).filter((x): x is number => typeof x === 'number');
};

const asSortField = (v: unknown): TourOperatorSortField | undefined =>
  typeof v === 'string' && sortFieldValueSet.has(v)
    ? (v as TourOperatorSortField)
    : undefined;

const asSortOrder = (v: unknown): SortOrder | undefined =>
  typeof v === 'string' && sortOrderValueSet.has(v)
    ? (v as SortOrder)
    : undefined;

export const validateTourOperatorsSearch = (
  input: Record<string, unknown>
): TourOperatorsSearch => ({
  search: typeof input.search === 'string' ? input.search : undefined,
  type: asTypeArray(input.type),
  city: asStringArray(input.city),
  rating: asNumberArray(input.rating),
  specials: asStringArray(input.specials),
  country: asStringArray(input.country),
  companyIds: asStringArray(input.companyIds),
  page: typeof input.page === 'number' ? input.page : undefined,
  pageSize: typeof input.pageSize === 'number' ? input.pageSize : undefined,
  sortBy: asSortField(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
});
